import { useMemo, useState } from 'react'

export type FloorId = 'laminado' | 'spc' | 'macizo'
export type SkirtingId = 'madera' | 'pvc'
export type ServiceId = 'completo' | 'material'
export type FurnitureId = 'si' | 'no'
export type RemovalId = 'si' | 'no'
export type LevelingId = 'apto' | 'autonivelar' | 'no-lo-se'

export interface CalculatorState {
  floor: FloorId
  skirting: SkirtingId
  meters: number
  service: ServiceId
  furniture: FurnitureId
  removal: RemovalId
  leveling: LevelingId
  doors: number
}

export const FLOOR_OPTIONS: {
  id: FloorId
  label: string
  detail: string
  price: number
}[] = [
  {
    id: 'laminado',
    label: 'Laminado',
    detail: 'Estética y resistencia con instalación rápida',
    price: 15,
  },
  {
    id: 'spc',
    label: 'Vinílico SPC',
    detail: 'Máxima resistencia al agua y al uso diario',
    price: 24,
  },
  {
    id: 'macizo',
    label: 'Macizo multicapa',
    detail: 'Madera natural, la opción más premium',
    price: 39,
  },
]

export const SKIRTING_OPTIONS: {
  id: SkirtingId
  label: string
  detail: string
  price: number
}[] = [
  { id: 'madera', label: 'Madera', detail: 'Cálido y lacable al color de la carpintería', price: 5 },
  { id: 'pvc', label: 'PVC', detail: 'Resistente a humedad y golpes', price: 7 },
]

export const LABOR_PRICE_PER_M2 = 24
export const FURNITURE_PRICE = 100
export const REMOVAL_PRICE_PER_M2 = 3
export const DOOR_PRICE = 20

export const INITIAL_STATE: CalculatorState = {
  floor: 'laminado',
  skirting: 'madera',
  meters: 60,
  service: 'completo',
  furniture: 'no',
  removal: 'no',
  leveling: 'apto',
  doors: 0,
}

export interface Breakdown {
  label: string
  detail: string
  amount: number
}

export interface CalculatorResult {
  total: number
  min: number
  max: number
  breakdown: Breakdown[]
  needsTechnicalReview: boolean
}

const roundTo = (value: number, step: number) => Math.round(value / step) * step

export function useCalculator() {
  const [state, setState] = useState<CalculatorState>(INITIAL_STATE)

  const set = <K extends keyof CalculatorState>(key: K, value: CalculatorState[K]) =>
    setState((prev) => ({ ...prev, [key]: value }))

  const reset = () => setState(INITIAL_STATE)

  const result = useMemo<CalculatorResult>(() => {
    const floor = FLOOR_OPTIONS.find((f) => f.id === state.floor) ?? FLOOR_OPTIONS[0]
    const skirting = SKIRTING_OPTIONS.find((s) => s.id === state.skirting) ?? SKIRTING_OPTIONS[0]

    const breakdown: Breakdown[] = [
      {
        label: `Material · ${floor.label}`,
        detail: `${state.meters} m² × ${floor.price} €/m²`,
        amount: state.meters * floor.price,
      },
      {
        label: `Rodapié · ${skirting.label}`,
        detail: `${state.meters} ml × ${skirting.price} €/ml`,
        amount: state.meters * skirting.price,
      },
    ]

    if (state.service === 'completo') {
      breakdown.push({
        label: 'Mano de obra · servicio completo',
        detail: `${state.meters} m² × ${LABOR_PRICE_PER_M2} €/m²`,
        amount: state.meters * LABOR_PRICE_PER_M2,
      })
    }

    if (state.furniture === 'si') {
      breakdown.push({
        label: 'Retirada y protección de mobiliario',
        detail: 'Importe fijo',
        amount: FURNITURE_PRICE,
      })
    }

    if (state.removal === 'si') {
      breakdown.push({
        label: 'Retirada de suelo antiguo',
        detail: `${state.meters} m² × ${REMOVAL_PRICE_PER_M2} €/m²`,
        amount: state.meters * REMOVAL_PRICE_PER_M2,
      })
    }

    if (state.doors > 0) {
      breakdown.push({
        label: 'Ajuste de puertas',
        detail: `${state.doors} × ${DOOR_PRICE} € por puerta`,
        amount: state.doors * DOOR_PRICE,
      })
    }

    const total = breakdown.reduce((sum, item) => sum + item.amount, 0)

    return {
      total,
      min: roundTo(total * 0.92, 10),
      max: roundTo(total * 1.12, 10),
      breakdown,
      needsTechnicalReview: state.leveling !== 'apto',
    }
  }, [state])

  return { state, set, reset, result }
}
